<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ChevronRight, Copy, Check, Share2 } from 'lucide-vue-next'
import ClaimInstructionsCard from '../ui/ClaimInstructionsCard.vue'
import { useNotifications } from '../../composables/useNotifications'

const { t: $t } = useI18n()
const { showSuccess, showError } = useNotifications()

const props = defineProps({
  claimUrl: {
    type: String,
    required: true
  },
  emojiPin: {
    type: String,
    required: true
  },
  walletAddress: {
    type: String,
    required: true
  },
  bgStyle: {
    type: Number,
    default: 0
  },
  isCardBack: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['continue'])

// Confirmation state
const backupConfirmed = ref(false)

// Can continue only if user confirmed backup
// TEMPORARILY DISABLED: Always allow continue without confirmation
const canContinue = computed(() => true) // Was: backupConfirmed.value

// Handle continue button
function handleContinue() {
  if (!canContinue.value) return
  emit('continue')
}

// Copy all instructions
const copiedAll = ref(false)
let copiedAllTimeout = null

async function handleCopyAll() {
  if (!props.claimUrl || !props.emojiPin) return

  const fullInstructions = `🎁 Crypto Gift Card - Claim Instructions

⚠️ IMPORTANT: Save this information securely!
If you lose the link and PIN, you will never be able to access the funds.

📎 Claim Link:
${props.claimUrl}

🔐 PIN Code:
${props.emojiPin}

Instructions:
1. Open the claim link
2. Enter the PIN code
3. Follow the instructions to withdraw your funds

⚠️ For security, share the link and PIN through different messaging apps.`

  try {
    await navigator.clipboard.writeText(fullInstructions)
    copiedAll.value = true
    showSuccess($t('share.allCopied', 'All instructions copied!'))

    if (copiedAllTimeout) clearTimeout(copiedAllTimeout)
    copiedAllTimeout = setTimeout(() => {
      copiedAll.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy all instructions:', err)
    showError($t('share.copyFailed', 'Failed to copy'))
  }
}

// Save to device
async function handleSaveToDevice() {
  if (!props.claimUrl || !props.emojiPin) return

  const fullInstructions = `🎁 Crypto Gift Card - Claim Instructions

⚠️ IMPORTANT: Save this information securely!
If you lose the link and PIN, you will never be able to access the funds.

📎 Claim Link:
${props.claimUrl}

🔐 PIN Code:
${props.emojiPin}

Instructions:
1. Open the claim link
2. Enter the PIN code
3. Follow the instructions to withdraw your funds

⚠️ For security, share the link and PIN through different messaging apps.`

  try {
    // Try native share first (mobile)
    if (navigator.share && navigator.canShare({ text: fullInstructions })) {
      await navigator.share({
        title: '🎁 Crypto Gift Card',
        text: fullInstructions
      })
      showSuccess($t('share.saved', 'Saved!'))
    } else {
      // Fallback: download as text file
      const blob = new Blob([fullInstructions], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `crypto-gift-card-${Date.now()}.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      showSuccess($t('share.saved', 'Saved to Downloads!'))
    }
  } catch (err) {
    if (err.name !== 'AbortError') {
      console.error('Failed to save:', err)
      showError($t('share.saveFailed', 'Failed to save'))
    }
  }
}
</script>

<template>
  <div class="secure-step">
    <!-- Card Back Content (only when isCardBack=true, used inside FlippableCard) -->
    <template v-if="isCardBack">
      <ClaimInstructionsCard
        :claim-url="claimUrl"
        :emoji-pin="emojiPin"
        :wallet-address="walletAddress"
        :bg-style="bgStyle"
        :is-card-back="true"
        :show-confirmation="false"
        :show-instructions="false"
      />
    </template>

    <!-- External Content (shown below the flipped card) -->
    <template v-else>
      <!-- Backup Section -->
      <div class="backup-section">
        <div class="backup-header">
          <span class="backup-icon">💾</span>
          <span class="backup-title">{{ $t('share.backupTitle', 'Backup Gift Card Details') }}</span>
        </div>
        <p class="backup-description">
          {{ $t('share.backupDescription', 'Save these credentials securely. No creds = No money.') }}
        </p>

        <!-- Backup Actions -->
        <div class="backup-actions">
          <button
            type="button"
            class="btn-backup btn-copy-all"
            :class="{ 'btn-copied': copiedAll }"
            @click="handleCopyAll"
          >
            <Check v-if="copiedAll" :size="18" />
            <Copy v-else :size="18" />
            <span>{{ copiedAll ? $t('share.copied', 'Copied!') : $t('share.copyAll', 'Copy All') }}</span>
          </button>

          <button
            type="button"
            class="btn-backup btn-save-device"
            @click="handleSaveToDevice"
          >
            <Share2 :size="18" />
            <span>{{ $t('share.saveToDevice', 'Save to Device') }}</span>
          </button>
        </div>
      </div>

      <!-- Security Warning -->
      <div class="security-warning">
        <div class="warning-emoji">⚠️</div>
        <p class="warning-text">
          {{ $t('share.criticalWarning', 'If you lose the link and the PIN you will never be able to access the funds on this card') }}
        </p>
      </div>

      <!-- Confirmation Checkbox -->
      <!-- TEMPORARILY DISABLED: Checkbox confirmation -->
      <!--
      <div class="confirmation-section">
        <label class="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            v-model="backupConfirmed"
            class="checkbox"
          />
          <span class="text-sm text-gray-700">
            {{ $t('share.confirmationSaved', 'I have saved the gift card details') }}
          </span>
        </label>
      </div>
      -->

      <!-- Continue Button -->
      <button
        type="button"
        class="btn-primary btn-continue"
        :disabled="!canContinue"
        @click="handleContinue"
      >
        <span>{{ $t('common.continue', 'Continue to Top Up') }}</span>
        <ChevronRight :size="20" />
      </button>
    </template>
  </div>
</template>

<style scoped>
.secure-step {
  @apply flex flex-col gap-5 w-full max-w-2xl mx-auto;
}

/* Backup Section */
.backup-section {
  @apply bg-blue-50 border-2 border-blue-200 rounded-xl p-4;
  @apply flex flex-col gap-3;
}

.backup-header {
  @apply flex items-center gap-2;
}

.backup-icon {
  @apply text-xl;
}

.backup-title {
  @apply text-base font-semibold text-gray-900;
}

.backup-description {
  @apply text-sm text-gray-700 m-0;
}

.backup-actions {
  @apply grid grid-cols-2 gap-2.5;
}

.btn-backup {
  @apply flex items-center justify-center gap-2;
  @apply px-4 py-3 rounded-lg;
  @apply font-medium text-sm;
  @apply transition-all duration-200;
  @apply cursor-pointer;
  @apply border-2;
}

.btn-copy-all {
  @apply bg-purple-500 border-purple-500 text-white;
}

.btn-copy-all:hover {
  @apply bg-purple-600 border-purple-600;
  transform: translateY(-2px);
  @apply shadow-lg;
}

.btn-copy-all.btn-copied {
  @apply bg-green-500 border-green-500;
}

.btn-save-device {
  @apply bg-blue-500 border-blue-500 text-white;
}

.btn-save-device:hover {
  @apply bg-blue-600 border-blue-600;
  transform: translateY(-2px);
  @apply shadow-lg;
}

.btn-backup:active {
  transform: translateY(0);
}

/* Security Warning */
.security-warning {
  @apply bg-gradient-to-r from-orange-50 to-red-50;
  @apply border-2 border-orange-300;
  @apply rounded-xl p-4;
  @apply flex items-center gap-4;
  @apply shadow-sm;
}

.warning-emoji {
  @apply text-4xl flex-shrink-0;
}

.warning-text {
  @apply text-sm font-medium text-gray-800 leading-relaxed m-0;
}

/* Confirmation Section */
.confirmation-section {
  @apply border-2 border-gray-200 rounded-xl p-4;
  @apply bg-white;
}

.checkbox {
  @apply w-5 h-5 rounded cursor-pointer;
  @apply border-2 border-gray-300;
  @apply transition-colors duration-200;
}

.checkbox:checked {
  @apply bg-blue-500 border-blue-500;
}

/* Continue Button */
.btn-continue {
  @apply flex items-center justify-center gap-2 w-full;
  @apply py-4 px-6 text-base font-semibold;
}

.btn-continue:disabled {
  @apply opacity-50 cursor-not-allowed;
}

/* Mobile responsive */
@media (max-width: 640px) {
  .secure-step {
    @apply gap-4;
  }

  .backup-section {
    @apply p-3;
  }

  .backup-icon {
    @apply text-lg;
  }

  .backup-title {
    @apply text-sm;
  }

  .backup-description {
    @apply text-xs;
  }

  .btn-backup {
    @apply px-3 py-2.5 text-xs;
  }

  .security-warning {
    @apply p-3 gap-3;
  }

  .warning-emoji {
    @apply text-3xl;
  }

  .warning-text {
    @apply text-xs;
  }
}
</style>
